/* eslint-disable @typescript-eslint/no-explicit-any */
import { useContext, useState } from "react";
import { DataTableWrapper } from "@/Components/datatable/data-table";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/Components/ui/tooltip"

import { FilePlus2 } from "lucide-react";
import { getActionColumns, columnDisplayNames } from "@/Components/Actions/actions_table/columns";
import { Action } from "@/Components/Actions/Action";
import { Card } from "../Components/ui/card";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/Components/ui/dialog";
import React from "react";
import { AppContext } from "@/App";
import Confirmation from "@/hooks/confirmation";
import PageLockProvider, { PageLockContext, PageName } from "@/Components/PageLockProvider";
import { formatDate } from "date-fns";
import HelpLink from "@/Components/ui/help-link";
import CSV_Importer from "@/Components/datatable/data_importer/csv_importer";
import ActionForm from "@/Components/Actions/actions_table/ActionForm";
import ActionPopup from "@/Components/Actions/actions_table/ActionPopup";
import { DeleteIcon, EditIcon } from "@/Components/ui/Icons/TooltipIcon";
import WizardProvider, { WizardContext } from "@/Components/wizard/wizardProvider";
import { getBasicPageTourViewOnlySteps, getBasicTourSteps } from "@/Components/Actions/actions_wizards/basic_page_tour";
import { ActionContext } from "@/Components/Actions/Action";
import { useResponsiveZoom } from "@/hooks/use-responsive-zoom";
import { useIsMobile } from "@/hooks/use-mobile";
import { Tutorial } from "@/lib/utils";
import TutorialChecker from "@/Components/Tutorial/TutorialChecker";

export type ActionColumnVisibility = {
    actionId: boolean
    date: boolean
    workstream: boolean
    workPackage: boolean
    topic: boolean
    raisedBy: boolean
    description: boolean
    status: boolean
    dueDate: boolean
    owner: boolean
    notes: boolean
    seen: boolean
    acknowledged: boolean
}

function ActionsPage() {
    const zoomLevel = useResponsiveZoom();
    const isMobile = useIsMobile();
    const app = useContext(AppContext);
    const lock = useContext(PageLockContext);
    const wizard = useContext(WizardContext);

    const [demoMode, setDemoMode] = useState(false);
    const useInlineEditing = app.applicationUser?.userSettings?.inlineTableEditing ?? false;

    const [dialogOpen, setDialogOpen] = React.useState(false);
    const [loading, setLoading] = useState<boolean>(true);

    const [dialogTitle, setDialogTitle] = useState("Group Edit Tasks");
    const [dialogTitleBarActions, setDialogTitleBarActions] = useState<JSX.Element | null>(null);
    const [dialogContent, setDialogContent] = useState<JSX.Element | null>(null);

    const users = app.teamMembers.concat(app.projectManagers).concat(app.sponsors);
    const [actionTableContent, setActionTableContent] = useState<Action[]>([]);
    const [actionTableDemoContent, setActionTableDemoContent] = useState<Action[]>([]);

    const [rowSelection, setRowSelection] = useState({});

    const [importedRows, setImportedRows] = useState<any[]>([]);

    React.useEffect(() => {
        if (importedRows.length > 0) {
            const newActions = importedRows.map((row) => {
                const newAction: Action = {
                    ...row,
                    actionId: 0,
                    projectId: app.selectedProject?.id || 0,
                    userId: users.find(u => u.displayName === row.owner)?.id || "",
                    workPackageId: app.workPackageOptions?.flatMap(w => w.workPackageNames).find(wp => wp.workPackageName === row.workPackage)?.workPackageId || 0,
                    workstreamId: app.workPackageOptions?.[0]?.workstreamId || 0,
                };
                return newAction;
            });
            saveActions(newActions);
            setImportedRows([]);
        }
    }, [importedRows]);

    const importDataType: { name: string, type: any, example: string }[] = [
        { name: "date", "type": Date, example: "2026-01-23" },
        { name: "workPackage", "type": String, example: "WP-1" },
        { name: "raisedBy", "type": String, example: "John Doe" },
        { name: "description", "type": String, example: "Action description" },
        { name: "owner", "type": String, example: "Jane Smith" },
        { name: "dueDate", "type": Date, example: "2026-01-27" },
        { name: "status", "type": String, example: "Open" },
        { name: "notes", "type": String, example: "Additional notes" }
    ];

    React.useEffect(() => {
        if (app.authLevel !== 3 && !lock.viewOnly) { // viewer cant see this page. Sponsor can see it but not edit it
            const down = (e: KeyboardEvent) => {
                if (e.key === "r" && (e.metaKey || e.ctrlKey)) {
                    e.preventDefault()
                    addAction();
                }
            }
            document.addEventListener("keydown", down)
            return () => document.removeEventListener("keydown", down)
        }
        populateDemoData();
    }, []);

    React.useEffect(() => {
        refreshActions();
    }, [app.selectedProject]);

    // Keep track of previous action table content for comparison
    const prevActionTableContentRef = React.useRef<Action[]>([]);

    React.useEffect(() => {
        // Skip on initial load or when refreshing all actions
        if (prevActionTableContentRef.current.length === 0 ||
            loading ||
            actionTableContent.length !== prevActionTableContentRef.current.length) {
            prevActionTableContentRef.current = [...actionTableContent];
            return;
        }

        // Find updated actions by comparing with previous state
        const updatedActions = actionTableContent.filter((currentAction) => {
            const prevAction = prevActionTableContentRef.current.find(a => a.actionId === currentAction.actionId);
            if (!prevAction) return false;

            // Compare all properties to detect changes
            return JSON.stringify(prevAction) !== JSON.stringify(currentAction);
        });

        // Save each updated action to the server
        updatedActions.forEach(action => {
            if (action.actionId > 0) { // Only update existing actions, not new ones
                updateAction(action);
            }
        });

        // Update ref with current state
        prevActionTableContentRef.current = [...actionTableContent];
    }, [actionTableContent]);

    function showFormDialog(selectedAction?: Action) {
        const action = selectedAction ? (
            <div onClick={(e) => {
                e.stopPropagation();
                if (selectedAction.actionId) {
                    confirmDeleteActions([selectedAction.actionId]);
                }
            }}
                className="p-2 rounded-md cursor-pointer hover:bg-red-600">
                <DeleteIcon className={`${isMobile ? "w-8 h-8" : "w-4 h-4"}`} />
            </div>
        ) : null;

        setDialogTitleBarActions(action);

        setDialogTitle(selectedAction ? "Edit Action" : "Create New Action");

        setDialogContent(
            <ActionForm
                saveAction={saveAction}
                action={selectedAction}
            />
        );
        setDialogOpen(true);
    }

    function showPopup(selectedAction: Action) {
        const actions = (
            <div className="flex flex-row">
                <div onClick={() => {
                    showFormDialog(selectedAction);
                }}
                    className="ml-2 p-2 rounded-md cursor-pointer hover:bg-blue-600"
                >
                    <EditIcon className={`${isMobile ? "w-8 h-8" : "w-4 h-4"}`} />
                </div>
                <div onClick={(e) => {
                    e.stopPropagation();
                    if (selectedAction.actionId) {
                        confirmDeleteActions([selectedAction.actionId]);
                    }
                }}
                    className="p-2 rounded-md cursor-pointer hover:bg-red-600">
                    <DeleteIcon className={`${isMobile ? "w-8 h-8" : "w-4 h-4"}`} />
                </div>
            </div>
        );

        setDialogTitle("Action Details");
        setDialogTitleBarActions(
            app.authLevel !== 3 && !lock.viewOnly ? actions : null
        );
        setDialogContent(
            <ActionPopup
                action={selectedAction}
            />
        );
        setDialogOpen(true);
    }

    function refreshActions() {
        if (app.selectedProject) {
            setLoading(true);
            document.body.style.cursor = 'wait';
            // fetch actions for project
            fetch("Raids/Actions/" + app.selectedProject.id)
                .then((response) => response.json())
                .then((data) => {
                    setActionTableContent(data);
                    setLoading(false);
                }
                ).catch((error) => {
                    app.showActionNotification("Error", "Error fetching actions: " + error.message, "destructive");
                })
                .finally(() => {
                    document.body.style.cursor = 'default';
                });
        }
    }

    function confirmDeleteActions(actionIds: number[]) {
        setDialogTitleBarActions(null);
        setDialogTitle(actionIds.length > 1 ? "Are you sure you want to delete these actions?" : "Are you sure you want to delete this action?");
        setDialogContent(<Confirmation onConfirm={() => { deleteActions(actionIds); setDialogOpen(false); }} onCancel={() => setDialogOpen(false)} confirmColour="bg-chart-red" />);
        setDialogOpen(true);
    }

    function editAction(id: number) {
        if (demoMode) {
            showFormDialog(actionTableDemoContent.find((a) => a.actionId === id));
            return;
        }
        showFormDialog(actionTableContent.find((a) => a.actionId === id));
    }

    function loadColumnVisibility(): ActionColumnVisibility {
        const localStorageValue = localStorage.getItem(app.applicationUser?.id + "_Actions_columnVisibility");
        if (localStorageValue) {
            const visibiltyValues: { columnId: string, isVisible: boolean }[] = JSON.parse(localStorageValue);
            return ({
                actionId: visibiltyValues.find((v) => v.columnId === "actionId")?.isVisible ?? false,
                date: visibiltyValues.find((v) => v.columnId === "date")?.isVisible ?? true,
                workstream: visibiltyValues.find((v) => v.columnId === "workstream")?.isVisible ?? false,
                workPackage: visibiltyValues.find((v) => v.columnId === "workPackage")?.isVisible ?? true,
                topic: visibiltyValues.find((v) => v.columnId === "topic")?.isVisible ?? false,
                raisedBy: visibiltyValues.find((v) => v.columnId === "raisedBy")?.isVisible ?? false,
                description: visibiltyValues.find((v) => v.columnId === "description")?.isVisible ?? true,
                status: visibiltyValues.find((v) => v.columnId === "status")?.isVisible ?? true,
                dueDate: visibiltyValues.find((v) => v.columnId === "dueDate")?.isVisible ?? true,
                owner: visibiltyValues.find((v) => v.columnId === "owner")?.isVisible ?? true,
                notes: visibiltyValues.find((v) => v.columnId === "notes")?.isVisible ?? false,
                seen: visibiltyValues.find((v) => v.columnId === "seen")?.isVisible ?? false,
                acknowledged: visibiltyValues.find((v) => v.columnId === "acknowledged")?.isVisible ?? false,
            })
        }
        return {
            actionId: false,
            date: true,
            workstream: false,
            workPackage: true,
            topic: false,
            raisedBy: false,
            description: true,
            status: true,
            dueDate: true,
            owner: true,
            notes: false,
            seen: false,
            acknowledged: false,
        }
    }

    function addAction() {
        if (useInlineEditing) {
            const newAction: Action = {
                actionId: 0,
                date: new Date(),
                projectId: app.selectedProject?.id || 0,
                workstream: app.workPackageOptions?.[0]?.workstreamName || "",
                workPackage: app.workPackageOptions?.[0]?.workPackageNames[0]?.workPackageName || "",
                workstreamId: app.workPackageOptions?.[0]?.workstreamId || 0, // default to first workstream
                workPackageId: app.workPackageOptions?.[0]?.workPackageNames[0]?.workPackageId || 0, // default to first work package
                raisedBy: "",
                description: "",
                status: "Open",
                dueDate: new Date(),
                owner: "",
                userId: "",
                notes: "",
                seen: false,
                acknowledged: false,
            };

            saveAction(newAction);
        } else {
            showFormDialog();
        }
    }

    function saveActions(actions: Action[]) {
        document.body.style.cursor = 'wait';
        if (demoMode) {
            const newActions = actions.map(action => ({ ...action, actionId: Math.floor(Math.random() * 1000000) }));
            setActionTableDemoContent([...newActions, ...actionTableDemoContent]);
            app.showActionNotification("Success", `${actions.length} Actions added from CSV`, "success");
            document.body.style.cursor = 'default';
            return;
        }
        fetch("/Raids/Actions", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(actions),
        })
            .then((response) => response.json())
            .then((data) => {
                setActionTableContent((prev) => [...data, ...prev]);
                app.showActionNotification("Success", `${actions.length} Actions added from CSV`, "success");
            })
            .catch((error) => {
                app.showActionNotification("Error", "Error adding actions: " + error.message, "destructive");
            })
            .finally(() => {
                document.body.style.cursor = 'default';
            });

    }

    function saveAction(action: Action) {
        if (action.actionId) {
            updateAction(action);
            return;
        }
        document.body.style.cursor = 'default';
        if (demoMode) {
            const newAction = { ...action, actionId: Math.floor(Math.random() * 1000000) };
            setActionTableDemoContent([newAction, ...actionTableDemoContent]);
            app.showActionNotification("Success", "Action Added Successfully!", "success");
            document.body.style.cursor = 'default';
            return;
        }
        fetch("/Raids/Action", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(action),
        })
            .then((response) => response.json())
            .then((data) => {
                setActionTableContent((prev) => [data, ...prev]);
                app.showActionNotification("Success", "Action Added Successfully!", "success");
            })
            .catch((error) => {
                app.showActionNotification("Error", "Error adding action: " + error.message, "destructive");
            })
            .finally(() => {
                document.body.style.cursor = 'default';
            });
    }

    function updateAction(action: Action) {
        document.body.style.cursor = 'wait';
        if (demoMode) {
            const newAction = { ...action, actionId: Math.floor(Math.random() * 1000000) };
            setActionTableDemoContent((prev) =>
                prev.map((a) => (a.actionId === action.actionId ? newAction : a))
            );
            app.showActionNotification("Success", "Action Updated Successfully!", "success");
            document.body.style.cursor = 'default';
            return;
        }
        fetch(`/Raids/Action/${action.actionId}`, {
            method: "PUT",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                ...action,
                date: formatDate(action.date, "yyyy-MM-dd"), // format date to YYYY-MM-DD stops js from applying daylight saving time
                dueDate: formatDate(action.dueDate, "yyyy-MM-dd"),
            }),
        })
            .then((response) => {
                if (response.ok) {
                    if (!useInlineEditing) {
                        // If not using inline editing, update the specific action in state
                        setActionTableContent((prev) =>
                            prev.map((a) => (a.actionId === action.actionId ? action : a))
                        );
                    }
                    app.showActionNotification("Success", "Action Updated Successfully!", "success");
                } else {
                    app.showActionNotification("Error", "Error updating action: " + response.statusText, "destructive");
                }
            })
            .catch((error) => {
                app.showActionNotification("Error", "Error updating action: " + error.message, "destructive");
            })
            .finally(() => {
                document.body.style.cursor = 'default';
            });
    }

    function deleteActions(ids: number[]) {
        document.body.style.cursor = 'wait';
        if (demoMode) {
            setActionTableDemoContent((prev) => prev.filter((a) => !ids.includes(a.actionId)));
            app.showActionNotification("Success", "Action Deleted", "success");
            document.body.style.cursor = 'default';
            return;
        }
        fetch("/Raids/Actions", {
            method: "DELETE",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(ids),
        })
            .then(() => {
                setActionTableContent(actionTableContent.filter((a) => !ids.includes(a.actionId)));
                app.showActionNotification("Success", "Action Deleted", "success");
            })
            .catch((error) => {
                app.showActionNotification("Error", "Error deleting action: " + error.message, "destructive");
            })
            .finally(() => {
                document.body.style.cursor = 'default';
            });
    }

    function getGroupActions() {
        const groupActions = app.authLevel > 3 ? [{
            name: "Delete",
            action: (selectedRows: any[]) => {
                confirmDeleteActions(selectedRows.map((row) => row.actionId));
            },
        }] : [];

        groupActions.push(
            {
                name: "Set Status to Open",
                action: (selectedRows: any[]) => {
                    const actionStatus = selectedRows.map((row) => ({ "id": row.actionId, "status": "Open" as "Closed" | "Cancelled" | "Open" }));
                    setStatus(actionStatus);
                },
            },
            {
                name: "Set Status to Closed",
                action: (selectedRows: any[]) => {
                    const actionStatus = selectedRows.map((row) => ({ "id": row.actionId, "status": "Closed" as "Closed" | "Cancelled" | "Open" }));
                    setStatus(actionStatus);
                },
            },
            {
                name: "Set Status to Cancelled",
                action: (selectedRows: any[]) => {
                    const actionStatus = selectedRows.map((row) => ({ "id": row.actionId, "status": "Cancelled" as "Closed" | "Cancelled" | "Open" }));
                    setStatus(actionStatus);
                },
            });
        return groupActions;
    }

    function setStatus(actionStatus: { id: number, status: "Closed" | "Cancelled" | "Open" }[]) {
        document.body.style.cursor = 'wait';
        if (demoMode) {
            setActionTableDemoContent(actionTableDemoContent.map((action) => {
                const updatedAction = actionStatus.find((as) => as.id === action.actionId);
                if (updatedAction) {
                    action.status = updatedAction.status;
                }
                return action;
            }));
            app.showActionNotification("Success", "Status Updated", "success");
            document.body.style.cursor = 'default';
            return;
        }
        fetch("/Raids/ActionStatus", {
            method: "PUT",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(actionStatus),
        })
            .then(() => {
                setActionTableContent(actionTableContent.map((action) => {
                    const updatedAction = actionStatus.find((as) => as.id === action.actionId);
                    if (updatedAction) {
                        action.status = updatedAction.status;
                    }
                    return action;
                }));
                app.showActionNotification("Success", "Status Updated", "success");
            })
            .catch((error) => {
                app.showActionNotification("Error", "Error updating status: " + error.message, "destructive");
            })
            .finally(() => {
                document.body.style.cursor = 'default';
            });
    }

    function populateDemoData() {
        const demoActions: Action[] = [];
        const owners = users.length > 0 ? users.map(u => u.displayName) : ["User 1", "User 2", "User 3"];
        const workPackages = app.workPackageOptions?.flatMap(w => w.workPackageNames).map(wp => wp.workPackageName) || ["WP-1", "WP-2", "WP-3"];
        for (let i = 0; i < 20; i++) {
            const randomOwner = owners[Math.floor(Math.random() * owners.length)];
            const randomRaisedBy = owners[Math.floor(Math.random() * owners.length)];
            const randomWorkPackage = workPackages[Math.floor(Math.random() * workPackages.length)];
            const newAction: Action = {
                actionId: i + 1,
                date: new Date(new Date().setDate(new Date().getDate() - Math.floor(Math.random() * 30))),
                projectId: app.selectedProject?.id || 0,
                workstream: app.workPackageOptions?.[0]?.workstreamName || "Default Stream",
                workPackage: randomWorkPackage,
                workstreamId: app.workPackageOptions?.[0]?.workstreamId || 0,
                workPackageId: app.workPackageOptions?.flatMap(w => w.workPackageNames).find(wp => wp.workPackageName === randomWorkPackage)?.workPackageId || 0,
                raisedBy: randomRaisedBy,
                description: `This is a demo action item number ${i + 1}`,
                status: Math.random() < 0.7 ? "Open" : (Math.random() < 0.5 ? "Closed" : "Cancelled"),
                dueDate: new Date(new Date().setDate(new Date().getDate() + Math.floor(Math.random() * 60))),
                owner: randomOwner,
                userId: users.find(u => u.displayName === randomOwner)?.id?.toString() || "",
                notes: Math.random() < 0.5 ? `These are some notes for action item number ${i + 1}` : "",
                seen: Math.random() < 0.5,
                acknowledged: Math.random() < 0.5,
            };
            demoActions.push(newAction);
        }
        setActionTableDemoContent(demoActions);
    }

    const demoButton = (
        <TooltipProvider delayDuration={1}>
            <Tooltip>
                <TooltipTrigger asChild>
                    <button id="exitDemoMode" className="rounded-md text-white bg-red-500 h-min px-2 my-auto" onClick={() => setDemoMode(false)}>Exit Demo Mode</button>
                </TooltipTrigger>
                <TooltipContent className="flex flex-col bg-background">
                    <div className="flex flex-row text-muted-foreground">
                        <p>
                            Demo mode allows you to try out the Actions page without making any actual changes to the data.
                        </p>
                        <p>
                            The table will be populated with some sample data and you can try out adding, editing, deleting and exporting Actions.
                        </p>
                    </div>
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );

    const contextValue = {
        dialogOpen,
        setDialogOpen,
        actionTableDemoContent,
        setActionTableDemoContent,
        showPopup,
        demoMode,
        setDemoMode,
        rowSelection,
        setRowSelection,
        addAction,
    }

    if (isMobile) {
        return (
            <ActionContext.Provider value={contextValue}>
                <Card className="mb-8 relative" style={{ zoom: zoomLevel }}>
                    {wizard && wizard.getWizardPointer()}
                    <div className="flex flex-row bg-table-actions justify-between px-2 rounded-t-lg">
                        <div className="flex flex-row">
                            <h1 className="text-4xl my-auto p-2">Actions</h1>
                            <div className="my-auto" onClick={refreshActions}>{lock.getRefreshControl()}</div>
                        </div>
                        <div className="flex flex-row">
                            {app.authLevel !== 3 && !lock.viewOnly ? (
                                <div className="flex justify-end">
                                    <TooltipProvider delayDuration={1}>
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <div
                                                    id="addNewAction"
                                                    onClick={() => addAction()}
                                                    className="rounded-full bg-table-actions border border-white text-table-header-foreground p-2 hover:cursor-pointer m-2"
                                                >
                                                    <FilePlus2 className="w-8 h-8" />
                                                </div>
                                            </TooltipTrigger>
                                            <TooltipContent className="flex flex-col bg-background">
                                                <div className="flex flex-row text-muted-foreground place-content-between">
                                                    <span className="p-2">Create New Action</span>
                                                </div>
                                            </TooltipContent>
                                        </Tooltip>
                                    </TooltipProvider>
                                </div>
                            ) : <div></div>}
                        </div>
                        <div className="flex flex-row absolute top-0 right-2 -translate-y-[130%]">{app.authLevel !== 3 && lock.getLockControl()}</div>
                    </div>
                    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                        <DialogContent id="action-dialog" className="flex flex-col w-[95%]" style={{ zoom: zoomLevel }} onInteractOutside={(e) => {
                            if (demoMode) {
                                e.preventDefault();
                            }
                        }}>
                            <DialogHeader>
                                <DialogTitle><div className="flex"><div className="text-4xl my-auto mr-2">{dialogTitle}</div>{dialogTitleBarActions}</div></DialogTitle>
                            </DialogHeader>
                            {dialogContent}
                        </DialogContent>
                    </Dialog>
                    <DataTableWrapper
                        columns={getActionColumns(
                            (ids: number[]) => confirmDeleteActions(ids),
                            (id: number) => editAction(id),
                        )}
                        data={demoMode ? actionTableDemoContent : actionTableContent}
                        setData={demoMode ? setActionTableDemoContent : setActionTableContent}
                        loading={loading}
                        viewOnly={app.authLevel === 3 || lock.viewOnly}
                        header_colour="bg-table-actions"
                        table_title="Actions"
                        columnVisibility={loadColumnVisibility()}
                        groupActions={getGroupActions()}
                        columnDisplayNames={columnDisplayNames}
                        onRowClick={!useInlineEditing ? (row) => { showPopup((row.original as Action)); } : undefined}
                        rowSelection={rowSelection}
                        setRowSelection={setRowSelection}
                    />
                </Card>
            </ActionContext.Provider>
        );
    }

    return (
        <ActionContext.Provider value={contextValue}>
            <Card className="p-2 m-2 relative" style={{ zoom: zoomLevel }}>
                {wizard && wizard.getWizardPointer()}
                <div className="flex flex-row justify-between">
                    <div className="flex flex-row"><h1 className="text-2xl my-auto p-2">Actions</h1><div className="my-auto" onClick={refreshActions}>{lock.getRefreshControl()}</div></div>
                    <div className="flex flex-row">{app.authLevel !== 3 && lock.getLockControl()}<HelpLink page="action" />{demoMode && demoButton}</div>
                    {
                        app.authLevel !== 3 && !lock.viewOnly ? ( // viewer cant see this page. Sponsor can see it but not edit it
                            <div className="flex justify-end">
                                <TooltipProvider delayDuration={1}>
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <div
                                                id="addNewAction"
                                                onClick={() => addAction()}
                                                className="rounded-full bg-table-actions text-table-header-foreground w-min p-2 hover:cursor-pointer m-2"
                                            >
                                                <FilePlus2 />
                                            </div>
                                        </TooltipTrigger>
                                        <TooltipContent className="flex flex-col bg-background">
                                            <div className="flex flex-row text-muted-foreground place-content-between">
                                                <span className="p-2">Create New Action</span>
                                            </div>
                                        </TooltipContent>
                                    </Tooltip>
                                </TooltipProvider>
                                {<CSV_Importer propertyTypes={importDataType} setNewRows={setImportedRows} bgColor="bg-table-actions" title="Import Actions from CSV"/>}
                            </div>
                        ) : <div></div>
                    }
                </div>
                <Dialog open={dialogOpen} onOpenChange={setDialogOpen} >
                    <DialogContent id="action-dialog" className="flex flex-col max-w-fit" onInteractOutside={(e) => {
                        if (demoMode) {
                            e.preventDefault();
                        }
                    }}
                    >
                        <DialogHeader>
                            <DialogTitle><div className="flex"><div className="my-auto mr-2">{dialogTitle}</div>{dialogTitleBarActions}</div></DialogTitle>
                        </DialogHeader>
                        {dialogContent}
                    </DialogContent>
                </Dialog>
                <TutorialChecker 
                    tutorialId={Tutorial.RAIDS} 
                    tutorial={async () => { 
                            setDemoMode(true); 
                            const acquiredLock = await lock.lockPage();
                            wizard.setWizardSteps(getBasicPageTourViewOnlySteps({ viewOnly: false, actionContext: contextValue })); 
                            wizard.setGuidedTourCompleteFunctionCall(() => () =>  { lock.unlockPage(acquiredLock?.id); setDemoMode(false); });
                            wizard.startGuidedTour(); }} />
                <DataTableWrapper
                    columns={getActionColumns(
                        (ids: number[]) => confirmDeleteActions(ids),
                        (id: number) => editAction(id), /*
                    (actionStatus: { id: number, status: "Closed" | "Cancelled" | "Open" }[]) => setStatus(actionStatus),*/
                    )}
                    data={demoMode ? actionTableDemoContent : actionTableContent}
                    setData={demoMode ? setActionTableDemoContent : setActionTableContent}
                    loading={loading}
                    viewOnly={app.authLevel === 3 || lock.viewOnly} // viewer can see but not edit
                    header_colour="bg-table-actions"
                    table_title="Actions"
                    columnVisibility={loadColumnVisibility()}
                    groupActions={getGroupActions()}
                    columnDisplayNames={columnDisplayNames}
                    onRowClick={!useInlineEditing ? (row) => { showPopup((row.original as Action)); } : undefined}
                    rowSelection={rowSelection}
                    setRowSelection={setRowSelection}
                />
                <div className="flex flex-row w-min ml-auto">{wizard.getTourControls()}</div>
            </Card>
        </ActionContext.Provider>
    );
}

function Actions() {
    const app = useContext(AppContext);
    return <PageLockProvider pageName={PageName.Actions} userId={app.applicationUser?.id as string} projectId={app.selectedProject?.id as number} >
        <WizardProvider wizardSteps={getBasicTourSteps()} >
            <ActionsPage />
        </WizardProvider>
    </PageLockProvider>;
}

export default Actions;